import { fixEncoding, findClasseMatch } from './importElevesUtils';

/**
 * Lecture du fichier d'affectation des groupes (Nom, Prénom, Classe, Groupe),
 * avant l'envoi à `groupes/initialisation-groupe-eleves/`.
 *
 * Le serveur applique « la ligne fait foi » (29/09/2026) : pour chaque élève
 * reçu, ses groupes de besoin (dans les matières citées par le fichier)
 * deviennent exactement ceux de ses lignes, et il est retiré des autres ; un
 * demi-groupe n'est jamais vidé.
 *
 * - Ligne entièrement vide (lignes vidées sans être supprimées, « ;;; » d'un
 *   CSV enregistré par Excel) : ignorée sans bruit. Elle bloquait tout
 *   l'import jusqu'au 29/09/2026 (« ligne incomplète »).
 * - Nom, Prénom et Classe remplis, Groupe vide : l'élève n'est pas envoyé
 *   (« sans groupe »), il garde ses groupes. Ce n'est pas une erreur : une
 *   liste de classe complète contient des élèves hors de tout groupe.
 * - Nom, Prénom ou Classe vide : ligne incomplète, ignorée ; si le nom et le
 *   prénom y figurent, les autres lignes de ce nom et prénom sont écartées
 *   aussi (cellules fusionnées, classe oubliée sur une ligne sur deux) : ce
 *   garde-fou-là reste.
 * - Un nom de groupe inconnu de TanoEd est IGNORÉ, les groupes reconnus de la
 *   ligne partent (décision du fondateur, 29/09/2026). Un vrai export Pronote
 *   liste tous les groupes de l'élève, langues et options comprises (6ESP1,
 *   LATIN, CHORALE…) : écarter l'élève entier laissait 39 élèves sur 79 non
 *   modifiés sur l'export simulé de l'établissement 15. Le prix, assumé : un
 *   groupe de besoin mal écrit fait quitter à l'élève son groupe de cette
 *   matière si le fichier la cite ailleurs ; le délégué corrige et réimporte
 *   (la ligne fait foi, rien d'autre que le lien n'est touché). Pour qu'il voie
 *   la faute, les noms ignorés sont rendus agrégés, avec leur nombre d'élèves.
 *   Une ligne dont AUCUN groupe n'est reconnu n'est pas envoyée (l'élève garde
 *   ses groupes).
 * - En-têtes, noms de groupes : comparés nettoyés (casse, accents, espaces),
 *   « Prénom », « PRENOM » et « Nom » suivi d'un espace se valent.
 */

/** Minuscules, sans accents, espaces réduits : pour comparer des en-têtes et des noms de groupes. */
export const normaliser = (s) => String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const LIBELLES = { nom: 'Nom', prenom: 'Prénom', classe: 'Classe', groupe: 'Groupe' };

/** La colonne que désigne un en-tête du fichier : 'nom' | 'prenom' | 'classe' | 'groupe', null sinon. */
const colonneDe = (entete) => {
    const n = normaliser(entete);
    if (n === 'groupe' || n === 'groupes') return 'groupe';
    if (n === 'nom' || n === 'prenom' || n === 'classe') return n;
    return null;
};

/** Les colonnes requises absentes des en-têtes (libellés à afficher) ; [] si tout y est. */
export const colonnesManquantes = (entetes) => {
    const presentes = new Set((entetes || []).map(colonneDe).filter(Boolean));
    return Object.keys(LIBELLES).filter((c) => !presentes.has(c)).map((c) => LIBELLES[c]);
};

/**
 * Les statuts qui laissent l'élève intact SANS être une erreur : pas de groupe
 * sur la ligne, ou aucun groupe connu de TanoEd (une liste de classe, un export
 * Pronote de langues et d'options). Ni alerte ni « import partiel ».
 */
export const STATUTS_SANS_ALERTE = new Set(['sans_groupe', 'aucun_reconnu']);

/** Ce que l'écran dit d'une ligne ; toute ligne qui n'est pas « OK » laisse son élève intact. */
export const libelleStatut = (l) => {
    switch (l.statut) {
        case 'ok': return 'OK';
        case 'incomplete': return `${l.manquants.join(', ')} manquant${l.manquants.length > 1 ? 's' : ''}`;
        case 'sans_groupe': return 'Aucun groupe sur la ligne';
        case 'aucun_reconnu': return 'Aucun groupe connu de TanoEd';
        case 'ecarte': return 'Une autre ligne de cet élève est incomplète';
        default: return '';
    }
};

const RAISONS = {
    incomplete: (l) => `ligne ${l.ligne} incomplète (${l.manquants.join(', ')})`,
    ecarte: () => 'une autre de ses lignes est incomplète',
    sans_groupe: () => 'aucun groupe sur sa ligne',
    aucun_reconnu: (l) => `aucun groupe connu de TanoEd (${l.groupesInconnus.join(', ')})`,
};

/**
 * Analyse les lignes lues dans le fichier (objets { en-tête: valeur }, comme
 * les rend readExcel / sheet_to_json).
 *
 * @param {Object[]} rows
 * @param {{ groupes: Object[], classes: Object[] }} contexte - les groupes que
 *   l'import reconnaît (AdminGroupe : les groupes de besoin seulement depuis le
 *   2026-09-30, un demi-groupe du fichier est ignoré) et les classes (pour
 *   relire « 6 A » en « 6A »).
 * @returns {{ lignes: Object[], affectations: Object[], nbElevesEnvoyes: number, nonModifies: Object[],
 *   nomsIgnores: { nom: string, eleves: number }[] }} - nomsIgnores : les noms de groupes inconnus
 *   de TanoEd, un par nom (casse et accents confondus), avec leur nombre d'élèves, du plus fréquent au
 *   plus rare : c'est là que le délégué repère une faute.
 */
export const analyserFichierGroupes = (rows, { groupes = [], classes = [] } = {}) => {
    const groupeParNom = new Map((groupes || []).map((g) => [normaliser(g.nom), g]));
    const lignes = [];

    (rows || []).forEach((row, index) => {
        const cellules = { nom: '', prenom: '', classe: '', groupe: '' };
        Object.keys(row || {}).forEach((entete) => {
            const colonne = colonneDe(entete);
            if (colonne && !cellules[colonne]) cellules[colonne] = String(row[entete] ?? '').trim();
        });
        // Le numéro de la ligne dans le tableur (sheet_to_json saute les lignes
        // vides : l'index ne suffit pas), en-tête en ligne 1.
        const numero = Number.isInteger(row?.__rowNum__) ? row.__rowNum__ + 1 : index + 2;
        if (!cellules.nom && !cellules.prenom && !cellules.classe && !cellules.groupe) return;

        const nom = fixEncoding(cellules.nom);
        const prenom = fixEncoding(cellules.prenom);
        const classeTrouvee = findClasseMatch(cellules.classe, classes);
        const classe = classeTrouvee ? classeTrouvee.nom : cellules.classe;
        const manquants = [!nom && 'Nom', !prenom && 'Prénom', !classe && 'Classe'].filter(Boolean);

        const nomsDeGroupes = cellules.groupe.split(',').map((g) => g.trim()).filter(Boolean);
        const groupesReconnus = [];
        const groupesInconnus = [];
        nomsDeGroupes.forEach((n) => {
            const g = groupeParNom.get(normaliser(n));
            if (!g) groupesInconnus.push(n);
            else if (!groupesReconnus.includes(g)) groupesReconnus.push(g);
        });

        // Un nom inconnu est ignoré : la ligne part avec ses groupes reconnus.
        let statut = 'ok';
        if (manquants.length > 0) statut = 'incomplete';
        else if (nomsDeGroupes.length === 0) statut = 'sans_groupe';
        else if (groupesReconnus.length === 0) statut = 'aucun_reconnu';

        lignes.push({
            ligne: numero, nom, prenom, classe,
            groupeOriginal: cellules.groupe, groupesReconnus, groupesInconnus, manquants, statut,
            cle: `${normaliser(nom)}|${normaliser(prenom)}|${normaliser(classe)}`,
            cleNomPrenom: `${normaliser(nom)}|${normaliser(prenom)}`,
        });
    });

    // Le garde-fou des lignes incomplètes : les autres lignes du même nom et
    // prénom ne partent pas non plus (elles feraient foi à elles seules, sans
    // les groupes de la ligne incomplète).
    const nomsPrenomsEnErreur = new Set(
        lignes.filter((l) => l.statut === 'incomplete' && l.nom && l.prenom).map((l) => l.cleNomPrenom),
    );
    lignes.forEach((l) => {
        if (l.statut === 'ok' && nomsPrenomsEnErreur.has(l.cleNomPrenom)) l.statut = 'ecarte';
    });

    // Les noms ignorés, agrégés : « 6ESP1 (32 élèves), LATIN 6 (8), 6emeG33 (1) ».
    const parNomIgnore = new Map();
    lignes.forEach((l) => {
        if (l.statut === 'incomplete') return;
        l.groupesInconnus.forEach((n) => {
            const cle = normaliser(n);
            if (!parNomIgnore.has(cle)) parNomIgnore.set(cle, { nom: n, eleves: new Set() });
            parNomIgnore.get(cle).eleves.add(l.cle);
        });
    });
    const nomsIgnores = [...parNomIgnore.values()]
        .map(({ nom, eleves }) => ({ nom, eleves: eleves.size }))
        .sort((a, b) => b.eleves - a.eleves || a.nom.localeCompare(b.nom, 'fr'));

    const vus = new Set();
    const affectations = [];
    lignes.filter((l) => l.statut === 'ok').forEach((l) => {
        l.groupesReconnus.forEach((g) => {
            const cle = `${l.cle}#${g.id}`;
            if (vus.has(cle)) return;
            vus.add(cle);
            affectations.push({ nom: l.nom, prenom: l.prenom, classe: l.classe, groupe_id: g.id });
        });
    });
    const envoyes = new Set(lignes.filter((l) => l.statut === 'ok').map((l) => l.cle));

    // Un élève par entrée : la première ligne qui explique pourquoi il n'est pas envoyé.
    const nonModifies = [];
    const dejaListes = new Set();
    lignes.forEach((l) => {
        if (l.statut === 'ok' || envoyes.has(l.cle)) return;
        const cle = l.statut === 'incomplete' && !(l.nom && l.prenom) ? `ligne-${l.ligne}` : l.cle;
        if (dejaListes.has(cle)) return;
        dejaListes.add(cle);
        nonModifies.push({ nom: l.nom, prenom: l.prenom, classe: l.classe, raison: RAISONS[l.statut](l), statut: l.statut });
    });

    return { lignes, affectations, nbElevesEnvoyes: envoyes.size, nonModifies, nomsIgnores };
};
